/** adapted from https://github.com/mantinedev/mantine/tree/master/src/mantine-hooks/src/use-scroll-into-view */

import { useCallback, useRef, useEffect, useState } from 'react';

export const getRelativePosition = ({
  target,
  parent,
  alignment,
  offset,
}: {
  target: Element;
  parent: HTMLDivElement | null;
  alignment: 'start' | 'end' | 'center';
  offset: number;
}): number => {
  if (!target || !parent) {
    return 0;
  }
  const parentPosition = parent.getBoundingClientRect();
  const targetPosition = target.getBoundingClientRect();

  const diff = targetPosition.top - parentPosition.top;

  if (diff === 0) {
    return 0;
  }

  if (alignment === 'start') {
    const distance = diff - offset;
    const shouldScroll = distance <= targetPosition.height * 1 || true;

    return shouldScroll ? distance : 0;
  }

  const parentHeight = parentPosition.height;

  if (alignment === 'end') {
    const distance = diff + offset - parentHeight + targetPosition.height;
    const shouldScroll = distance >= -targetPosition.height * 1 || true;

    return shouldScroll ? distance : 0;
  }

  if (alignment === 'center') {
    return diff - parentHeight / 2 + targetPosition.height / 2;
  }

  return 0;
};

const easeInOutQuad = (t: number) =>
  t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

export const useScrollIntoView = () => {
  /** duration of scroll in milliseconds */
  const duration = 250;

  /** custom mathematical easing function */
  const easing = easeInOutQuad;

  /** additional distance between nearest edge and element */
  const offset = 32;

  const frameID = useRef(0);
  const startTime = useRef(0);
  const shouldStop = useRef(false);

  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (window && typeof window !== undefined && 'media' in window.matchMedia) {
      setReducedMotion(
        window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      );
    }
  }, []);

  const cancel = (): void => {
    if (frameID.current) {
      cancelAnimationFrame(frameID.current);
    }
  };

  const scrollIntoView = useCallback(
    ({
      target,
      scrollableRef,
    }: {
      target: Element;
      scrollableRef: HTMLDivElement | null;
    }) => {
      shouldStop.current = false;

      // stop existing animations if the function is called again
      if (frameID.current) {
        cancel();
      }

      const start = scrollableRef?.scrollTop ?? 0;

      const change = getRelativePosition({
        parent: scrollableRef,
        target,
        alignment: 'start',
        offset,
      });

      // no need to animate if the scrollable elemet is not overflowing
      if (
        scrollableRef &&
        scrollableRef.scrollHeight === scrollableRef.clientHeight
      ) {
        return;
      }

      function animateScroll() {
        if (startTime.current === 0) {
          startTime.current = performance.now();
        }

        const now = performance.now();
        const elapsed = now - startTime.current;

        // easing timing progress
        const t = reducedMotion ? 1 : elapsed / duration;

        const distance = start + change * easing(t);

        if (scrollableRef) {
          // eslint-disable-next-line no-param-reassign
          scrollableRef.scrollTop = distance;
        }

        if (!shouldStop.current && t < 1) {
          frameID.current = requestAnimationFrame(animateScroll);
        } else {
          startTime.current = 0;
          frameID.current = 0;
          cancel();
        }
      }
      animateScroll();
    },
    [duration, easing, offset, reducedMotion],
  );

  // cleanup requestAnimationFrame
  useEffect(() => cancel, []);

  return scrollIntoView;
};
